import styled from "styled-components"
import { TableEntry } from "../../models/leagueStats"
import { FC } from "react"
import placeholderImage from "../../images/common/vgc-logo.png"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTwitch } from "@fortawesome/free-brands-svg-icons";

interface Props {
  teams: TableEntry[]
  division: number
  showLiveLinks?: boolean;
}

const Table: FC<Props> = ({ teams, division, showLiveLinks=false }) => {
  const overlayColor = "rgba(0, 0, 190, 0)"
  const yellow = "hsla(120, 84%, 35%, 0.72)"
  const red = "hsla(0, 89%, 24%, 0.79)"
  const blue = "hsla(212, 83%, 50%, 1.00)"

  const setColor = (i: number) => {
    const numTeams = teams.length;
    const numPromoted = division === 1 ? 1 : 2;
    const numRelegated = 2;

    if (i < numPromoted) {
      return yellow;
    } else if (i >= numTeams - numRelegated) {
      return red;
    } else {
      return blue;
    }
  };

  return (
    <>
      {teams ? (
        <div
          className={
            "d-flex flex-column align-items-center justify-content-center"
          }
        >
          <Container>
            <Overlay color={overlayColor}>
              <UL>
                <TH>
                  <Col1>No</Col1>

                  <Col2>
                    <TeamName>TEAM</TeamName>
                  </Col2>

                  <Col>P</Col>
                  <Col>W</Col>
                  <Col>D</Col>
                  <Col>L</Col>
                  <Col>GD</Col>
                  <Col>PTS</Col>
                </TH>
                <HeaderUnderline color={blue}></HeaderUnderline>
                {teams.map((team, i) => (
                  <Li style={{ backgroundColor: setColor(i) }} key={i}>
                    <Col1>{i + 1}</Col1>

                    <Col2>
                      <img src={team.image || placeholderImage} alt={team.name} style={{ width: '20px', height: '20px', marginRight: '5px', verticalAlign: 'middle' }} />
                      <TeamName>{team.name}</TeamName>
                      {showLiveLinks && team.liveLink && (
                        <LiveLinkButton href={team.liveLink} target="_blank" rel="noopener noreferrer" className="btn btn-sm">
                          <FontAwesomeIcon icon={faTwitch} />
                        </LiveLinkButton>
                      )}
                    </Col2>
                    <Col>{team.P}</Col>
                    <Col>{team.W}</Col>
                    <Col>{team.D}</Col>
                    <Col>{team.L}</Col>
                    <Col>{team.GD}</Col>
                    <Col>{team.PTS}</Col>
                  </Li>
                ))}
              </UL>
              <KeyContainer>
                <KeyItem>
                  <KeyColor color={yellow}></KeyColor>
                  <KeyText>
                    {division && division === 1 ? "Champion" : "Promotion"}
                  </KeyText>
                </KeyItem>
                <KeyItem>
                  <KeyColor color={"rgba(153, 31, 31, 1)"}></KeyColor>
                  <KeyText>Relegation</KeyText>
                </KeyItem>
              </KeyContainer>
            </Overlay>
          </Container>
        </div>
      ) : null}
    </>
  )
}

export default Table

const Container = styled.div`
  width: 100%;
  background-size: cover;
  @media (max-width: 1200px) {
    font-size: small;
  }
  max-width: 870px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`
const Overlay = styled.div`
  width: 100%;
  background-color: ${(props) => props.color};
  height: 100%;
  border-radius: 16px;
`
const UL = styled.div`
  list-style-type: none;
  padding: 0 0.5rem;
  text-align: center;
  @media (max-width: 575px) {
    padding: 0 0;
  }
`
const Li = styled.div`
  border-radius: 12px;
  padding: 5px 1rem;
  display: flex;
  justify-content: space-between;
  margin: 0 0.5rem 6px 0.5rem;
  font-size: 0.70em;
  @media (max-width: 575px) {
    padding: 8px 0.5rem;
  }
  border: 1px solid rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
  box-shadow: 
    0 4px 12px rgba(0, 0, 0, 0.3),
    0 8px 24px rgba(0, 0, 0, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);

  &:hover {
    border-color: rgba(255, 255, 255, 0.2);
    box-shadow: 
      0 8px 20px rgba(0, 0, 0, 0.4),
      0 12px 32px rgba(0, 0, 0, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.1);
    transform: translateY(-3px);
  }
`
const TH = styled.div`
  background: transparent;
  letter-spacing: 0.03em;
  padding: 16px 1.5rem 8px 1.5rem;
  display: flex;
  justify-content: space-between;
  font-weight: 600;
  font-size: 0.70em;
  border-bottom: none;

  @media (max-width: 575px) {
    padding: 16px 1rem 8px 1rem;
    letter-spacing: normal;
  }
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
`
const HeaderUnderline = styled.div`
  width: 100%;
  background: transparent;
  margin-bottom: 0rem;
  border-radius: 2px;
`
const Col1 = styled.div`
  padding: 0 0 0 1rem;
  flex-basis: 5%;
  @media (max-width: 575px) {
    flex-basis: 6%;
    padding: 0 0 0 0;
  }
`
const Col2 = styled.div`
  flex-basis: 65%;
  padding-right: 4rem;
  padding-left: 0.5rem;
  @media (max-width: 768px) {
    flex-basis: 53%;
  }
  @media (max-width: 575px) {
    flex-basis: 52%;
    padding-right: 1rem;
  }
  @media (max-width: 480px) {
    flex-basis: 46%;
  }
  display: flex;
  justify-content: center;
  overflow: hidden;
`
const TeamName = styled.p`
  padding: 0;
  margin: 0;
  max-width: 200px;
  text-align: center;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  display: block;
`
const Col = styled.div`
  flex-basis: 5%;
  @media (max-width: 768px) {
    flex-basis: 6%;
  }
  @media (max-width: 575px) {
    flex-basis: 7%;
  }
  @media (max-width: 480px) {
    flex-basis: 8%;
  }
`
const KeyContainer = styled.div`
  padding: 1rem 0 1rem 2rem;
  @media (max-width: 575px) {
    padding: 0 0 1rem 0.5rem;
  }
`
const KeyItem = styled.div``
const KeyColor = styled.span`
  width: 10px;
  height: 10px;
  background-color: ${(props) => props.color};
  display: inline-block;
  margin: 0 1rem;
`
const KeyText = styled.span``

const LiveLinkButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  background-color: #6441a5;
  color: #fff;
  border-radius: 4px;
  padding: 12px 6px;
  text-decoration: none;
  margin-left: 10px;
  transition: background-color 0.3s ease;
  height: 20px;

  &:hover {
    background-color: #4e337f;
    text-decoration: none;
    color: #fff;
  }
`;
